/*
 * The MIT License
 *
 * Copyright (c) 2019- Nordic Institute for Interoperability Solutions (NIIS)
 * Copyright (c) 2018 Estonian Information System Authority (RIA),
 * Nordic Institute for Interoperability Solutions (NIIS), Population Register Centre (VRK)
 * Copyright (c) 2015-2017 Estonian Information System Authority (RIA), Population Register Centre (VRK)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
import axios from 'axios';
import { Backup, BackupRestorationStatus } from '@/openapi-types';
import { defineStore } from 'pinia';
import { saveResponseAsFile } from '@/util/helpers';

export interface State {
  backups: Backup[];
}

export const useBackups = defineStore('backup', {
  state: (): State => ({
    backups: [],
  }),
  persist: true,
  actions: {
    getBackups() {
      return axios
        .get<Backup[]>('/backups')
        .then((resp) => (this.backups = resp.data))
        .catch((error) => {
          throw error;
        });
    },
    createBackup() {
      return axios.post<Backup>('/backups').catch((error) => {
        throw error;
      });
    },
    deleteBackup(filename: string) {
      return axios.delete(`/backups/${filename}`).catch((error) => {
        throw error;
      });
    },
    restoreFromBackup(filename: string) {
      return axios
        .put<BackupRestorationStatus>(`/backups/${filename}/restore`)
        .catch((error) => {
          throw error;
        });
    },
    downloadBackup(filename: string) {
      return axios
        .get(`/backups/${filename}/download`, {
          responseType: 'blob',
        })
        .then((resp) => {
          saveResponseAsFile(resp, 'backup.tar');
        })
        .catch((error) => {
          throw error;
        });
    },
    uploadBackup(file: File, ignoreWarnings: boolean) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          ignore_warnings: ignoreWarnings,
        },
      };
      return axios.post<Backup>(`/backups/upload`, formData, config);
    },
  },
});
